// Staff incoming-call screen (roadmap #41), route /staff/incomingcall. Ported from
// qr-phone-frontend incomingcall.component.tsx (v3.2.0.0). The socket protocol is
// unchanged: ringtone + accept/reject, honours `kill-notification` (another staff
// answered) and the no-answer timeout, and NEVER calls socket.disconnect() on reject
// (that killed auto-reconnection — #23 regression guard). User-facing text is localised.
import { useEffect, useRef, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { child, get, off, onValue, ref, update } from 'firebase/database'
import { database } from '../core/firebase'
import { globals } from '../core/globals'
import { socket } from '../core/socket'
import { getAuthUser } from '../core/auth'
import { updateCallRealTime, updateCallStatus, updateCallStatusByUserId } from '../core/callStatus'
import { t } from '../i18n'

// PRETTY-PRINT A CALLER FORM LABEL.
const capitalize = (a: any) => (a ? a.toString().charAt(0).toUpperCase() + a.toString().slice(1) : a)

const StaffIncomingCall = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const user = getAuthUser()
  const user_id = globals.user_id
  const email = globals.email

  const [caller, setCaller] = useState<any>(globals.callerDetail)
  const [isCallActive, setIsCallActive] = useState(false)
  const countDownTimer = useRef<any>(null)
  const ringtoneRef = useRef<HTMLAudioElement | null>(null)
  // GUARD SO AN AUTO ACCEPT/REJECT FROM THE OS NOTIFICATION (#55) FIRES AT MOST ONCE.
  const autoActionDone = useRef(false)

  const databaseRef = ref(database, 'call')

  // STOP THE RINGTONE AND THE NO-ANSWER TIMER. RELIABLE STOP (#52): also stop any element
  // with the ringtone id, in case the ref was lost across a remount, so the audio never
  // outlives the call by depending on unmount-cleanup timing.
  const cancelCountDownTimer = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause()
      ringtoneRef.current.currentTime = 0
    }
    try {
      const el = document.getElementById('ringtone') as HTMLAudioElement | null
      if (el) {
        el.pause()
        el.currentTime = 0
      }
    } catch {
      /* no-op */
    }
    clearTimeout(countDownTimer.current)
    countDownTimer.current = null
  }

  // REJECT / KILL / MISS. isKillNotification = stop silently (another staff answered);
  // isRejected = the staff pressed Reject (emit reject-call to the caller).
  const rejectCall = async (isKillNotification: boolean, isRejected: boolean, reason: string) => {
    cancelCountDownTimer()

    if (!isKillNotification && isRejected) {
      const callerSocketID = globals.callerSocketID
      const staffs = globals.callStaffData
      try {
        await updateCallStatusByUserId(globals.call_id, 'REJTD', user_id)

        const isDefault =
          (globals as any)?.isThisDefaultStaffNotified === true ||
          (globals as any)?.isThisDefaultStaffNotified === 'true'

        if (!isDefault) {
          // PERSIST rejectedby_user_id (NORMALISED, DEDUPED) FOR NON-DEFAULT STAFF.
          const callRef = child(databaseRef, globals.call_id)
          let fetched: any = {}
          try {
            const snapshot = await get(callRef)
            fetched = snapshot.val() || {}
          } catch (e) {
            console.error('Failed to read call node before rejection update:', e)
          }
          const current = fetched.rejectedby_user_id
          let rejectedList: any[] = Array.isArray(current)
            ? current
            : typeof current === 'object' && current !== null
            ? Object.values(current)
            : []
          rejectedList = rejectedList.map((x: any) => Number(x)).filter((x: any) => !Number.isNaN(x))
          const myId = Number(user_id)
          if (!rejectedList.includes(myId)) rejectedList.push(myId)
          rejectedList = Array.from(new Set(rejectedList))
          try {
            await updateCallRealTime(globals.call_id, { rejectedby_user_id: rejectedList, reason })
          } catch (e) {
            console.error('Failed to update rejectedby_user_id:', e)
          }
        } else {
          // DEFAULT-STAFF PATH: UPDATE defaultstaffnotified ONLY.
          try {
            const defaultNotifiedRef = child(child(databaseRef, globals.call_id), 'defaultstaffnotified')
            const snapshotDefault = await get(defaultNotifiedRef)
            const notified = snapshotDefault.val() || {}
            const statuses = Array.isArray(notified.callstatuses) ? notified.callstatuses : []
            if (!statuses.includes('REJTD')) statuses.push('REJTD')
            await update(defaultNotifiedRef, { callstatus: 'REJTD', callstatuses: statuses, reason })
          } catch (e) {
            console.error('Error updating defaultstaffnotified on reject:', e)
          }
        }
      } catch (error) {
        console.error('rejectCall error:', error)
      }

      const rejection = { call_id: globals.call_id, type: reason, callstatus: 'REJTD', callee_email: email }
      socket.emit('reject-call', staffs, callerSocketID, email, rejection)
    }

    // KEEP THE SHARED SOCKET ALIVE (MANUAL disconnect() WOULD DISABLE AUTO-RECONNECTION AND
    // LEAVE THE STAFF UNABLE TO ANSWER THE NEXT CALL). JUST DROP THIS SCREEN'S LISTENERS.
    unsubscribeSocketEvents()

    // CLEAR THE CALL STATE SO A NEW INCOMING CALL IS NEVER CONFUSED WITH THE REJECTED ONE.
    globals.call_id = null
    globals.callerSocketID = null
    globals.callStaffData = null
    globals.callerDetail = {}
    globals.isThisDefaultStaffNotified = false
    globals.hasAnswered = false

    navigate('/staff/dashboard')
  }

  const handleKillNotification = () => {
    rejectCall(true, false, 'Kill Notification')
  }

  const subscribeSocketEvents = () => {
    if (socket) {
      if (!socket.connected) socket.connect()
      socket.on('kill-notification', handleKillNotification)
    }
  }

  const unsubscribeSocketEvents = () => {
    if (socket) socket.off('kill-notification', handleKillNotification)
  }

  // ARM THE SCREEN: RING STATUS, SOCKET LISTENERS, RINGTONE AND THE NO-ANSWER TIMER.
  useEffect(() => {
    if (!globals.call_id) return

    updateCallStatus(globals.call_id, 'RING').then()
    updateCallStatusByUserId(globals.call_id, 'RING', user_id).then()
    subscribeSocketEvents()

    // REACHABILITY SIGNAL (#52): this device's incoming-call UI actually rendered and is ringing.
    // The server uses it to tell a reachable agent apart from a stale push token (accepted but
    // never ringing), so an all-reject can end the caller without waiting for the no-answer timeout.
    if (socket && globals.call_id && email) {
      socket.emit('call-ringing-ack', { call_id: globals.call_id, email })
    }

    countDownTimer.current = setTimeout(async () => {
      if (!isCallActive && !globals.isCallActive) {
        if (ringtoneRef.current) {
          ringtoneRef.current.pause()
          ringtoneRef.current.currentTime = 0
        }
        await updateCallStatusByUserId(globals.call_id, 'MISS', user_id)
        rejectCall(false, false, '')
      }
    }, (globals.maxResponseSeconds || 45) * 1000)

    if (globals.callerDetail) setCaller(globals.callerDetail)

    const audio = new Audio('/ringtone.mp3')
    audio.loop = true
    audio.id = 'ringtone'
    ;(audio as any).playsInline = true
    audio.play().catch(() => {
      /* AUTOPLAY MAY BE BLOCKED UNTIL THE STAFF INTERACTS WITH THE TAB */
    })
    ringtoneRef.current = audio

    return () => {
      clearTimeout(countDownTimer.current)
      audio.pause()
      audio.currentTime = 0
      unsubscribeSocketEvents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globals.call_id])

  // WATCH THE CALL STATUS: IF SOMEONE ELSE ANSWERED (ANSRD/ACCPT) STOP RINGING.
  useEffect(() => {
    if (!globals.call_id) return
    const callStatusRef = child(child(databaseRef, globals.call_id), 'callstatus')
    const onStatus = (snapshot: any) => {
      const callDetail = snapshot.val()
      if (globals.hasAnswered) return
      // ANOTHER STAFF ANSWERED: STOP RINGING SILENTLY.
      if (['ANSRD', 'ACCPT'].indexOf(callDetail) !== -1) {
        rejectCall(true, false, 'Kill Notification')
        return
      }
      // THE CALL REACHED A TERMINAL STATE (CALLER HUNG UP / NO ANSWER / REJECTED ELSEWHERE):
      // STOP RINGING RELIABLY NOW instead of waiting for the no-answer timeout or unmount (#52).
      if (['ENDED', 'MISS', 'REJTD'].indexOf(callDetail) !== -1) {
        rejectCall(true, false, 'Call ended')
      }
    }
    onValue(callStatusRef, onStatus)
    return () => off(callStatusRef, 'value', onStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globals.call_id])

  // ACCEPT THE CALL: GUARD AGAINST A RACE (ANOTHER STAFF ALREADY ANSWERED) THEN GO TO THE
  // IN-CALL SCREEN, WHICH EMITS call-answered AND DRIVES THE MEDIA SETUP.
  const answerCall = async () => {
    const callIdRef = child(databaseRef, globals.call_id)
    const answeredData = await get(child(callIdRef, 'answaredby_fullname'))
    const answeredByUser = answeredData.val()

    cancelCountDownTimer()

    if (typeof answeredByUser === 'undefined' || answeredByUser == null) {
      setIsCallActive(true)
      globals.isCallActive = true
      globals.activeCallId = 1
      globals.activeCallStatus = 'CONNECTED'
      globals.hasAnswered = true

      const answeredBy = {
        answeredby_user_id: user_id,
        answaredby_fullname: user?.full_name,
        isCallConnected: 1,
      }
      updateCallStatusByUserId(globals.call_id, 'ACCPT', user_id)
      await updateCallStatus(globals.call_id, 'ACCPT', answeredBy)

      navigate('/staff/callcomponent', {
        state: {
          callerName: caller?.name,
          organizationName: caller?.organization_name,
          qrName: caller?.qrcode_name,
        },
      })
    } else {
      toast.error(t.staffAlreadyAnswered)
      navigate('/staff/dashboard')
    }
  }

  // AUTO ACCEPT / REJECT WHEN THE CALL WAS ACTIONED FROM THE OS NOTIFICATION (#55). The service
  // worker bridge navigates here with autoAccept/autoReject in the location state; we run the same
  // in-app answer/reject path so the notification buttons behave exactly like the in-app buttons.
  useEffect(() => {
    const st: any = location.state
    if (!st || autoActionDone.current || !globals.call_id) return
    if (st.autoAccept) {
      autoActionDone.current = true
      answerCall()
    } else if (st.autoReject) {
      autoActionDone.current = true
      rejectCall(false, true, 'Call Rejection')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // NO ACTIVE CALL (E.G. RELOADED THIS ROUTE DIRECTLY): SEND THE STAFF BACK TO THE DASHBOARD.
  if (!globals.call_id) {
    return (
      <div className="cf-page">
        <div className="cf-card cf-staff-card">
          <BrandLockup />
          <p className="cf-subtitle">{t.staffNoIncomingCall}</p>
          <button className="cf-btn cf-btn-secondary" onClick={() => navigate('/staff/dashboard')}>
            {t.back}
          </button>
        </div>
      </div>
    )
  }

  const fields =
    caller && typeof caller?.forminput === 'object' && caller.forminput
      ? Object.entries(caller.forminput)
      : []

  return (
    <div className="cf-page cf-staff-incoming">
      <div className="cf-card cf-staff-card">
        <BrandLockup />
        <div className="cf-incoming-pulse">📞</div>
        <h1 className="cf-title">{t.staffIncomingTitle}</h1>
        {caller?.organization_name && <p className="cf-incoming-org">{caller.organization_name}</p>}
        {caller?.qrcode_name && <p className="cf-subtitle">{caller.qrcode_name}</p>}

        {fields.length > 0 ? (
          <div className="cf-incoming-fields">
            {fields.map(([key, value]) => (
              <div key={key} className="cf-incoming-field">
                <span className="cf-incoming-field-label">{capitalize(key)}:</span> {String(value)}
              </div>
            ))}
          </div>
        ) : (
          <div className="cf-incoming-fields">
            {caller?.name && <div className="cf-incoming-field">{caller.name}</div>}
            {caller?.email && <div className="cf-incoming-field">{caller.email}</div>}
            {caller?.phone && <div className="cf-incoming-field">{caller.phone}</div>}
          </div>
        )}

        <div className="cf-incoming-actions">
          <button className="cf-btn cf-btn-danger" onClick={() => rejectCall(false, true, 'Call Rejection')}>
            {t.staffReject}
          </button>
          <button className="cf-btn cf-btn-primary" onClick={answerCall}>
            {t.staffAccept}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StaffIncomingCall
